// src/components/AgreementScreen.jsx
import { useState, useRef, useEffect } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { TERMS_TITLE, TERMS_SUBTITLE, TERMS_TEXT } from '../content/terms.ru';

const AgreementScreen = ({ onAccept }) => {
  const [canAccept, setCanAccept] = useState(false);
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const { tg } = useTelegram();

  const HEADER_HEIGHT_FALLBACK = 132;
  const FOOTER_HEIGHT_FALLBACK = 112;
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT_FALLBACK);
  const [footerHeight, setFooterHeight] = useState(FOOTER_HEIGHT_FALLBACK);
  const FADE_HEIGHT = 100;
  const HEADER_FADE_HEIGHT = 0;

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const denom = Math.max(1, scrollHeight - clientHeight);
    const p = Math.min(1, Math.max(0, scrollTop / denom));
    setProgress(p);
    if (scrollTop + clientHeight >= scrollHeight - 30) setCanAccept(true);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();
      return () => el.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  useEffect(() => {
    const headerEl = headerRef.current;
    const footerEl = footerRef.current;
    if (!headerEl && !footerEl) return;

    const applySizes = () => {
      if (headerEl) setHeaderHeight(Math.ceil(headerEl.getBoundingClientRect().height) || HEADER_HEIGHT_FALLBACK);
      if (footerEl) setFooterHeight(Math.ceil(footerEl.getBoundingClientRect().height) || FOOTER_HEIGHT_FALLBACK);
    };

    applySizes();

    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => applySizes());
    if (headerEl) ro.observe(headerEl);
    if (footerEl) ro.observe(footerEl);

    return () => ro.disconnect();
  }, []);

  // Плавное затухание кнопки
  const buttonOpacity = canAccept ? 1 : 0.45;
  const buttonBg = canAccept
    ? 'bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15]'
    : 'bg-[#2A2A2A]';
  const buttonText = canAccept ? 'text-white' : 'text-[#8A8A8A]';
  const buttonHover = canAccept ? 'hover:brightness-110' : '';
  const buttonCursor = canAccept ? 'cursor-pointer' : 'cursor-not-allowed';

  const handleAccept = () => {
    if (!canAccept) return;
    if (tg?.HapticFeedback?.notificationOccurred) {
      tg.HapticFeedback.notificationOccurred('success');
    }
    onAccept?.();
  };

  const termsParagraphs = TERMS_TEXT.split(/\n\s*\n/g).map((p) => p.trim()).filter(Boolean);

  const isSectionTitle = (p) => {
    if (!p) return false;
    if (/^\d+\./.test(p)) return true;
    if (/^[A-ZА-ЯЁ\s-]{6,}$/.test(p) && !/[a-zа-яё]/.test(p)) return true;
    if (/^ТЕРМИНЫ И ОПРЕДЕЛЕНИЯ$/.test(p)) return true;
    if (/^ПРЕДМЕТ ДОГОВОРА$/.test(p)) return true;
    if (/^ПОРЯДОК ОКАЗАНИЯ УСЛУГ$/.test(p)) return true;
    if (/^СТОИМОСТЬ УСЛУГ И ПОРЯДОК РАСЧЕТОВ$/.test(p)) return true;
    if (/^ПРАВА И ОБЯЗАННОСТИ СТОРОН$/.test(p)) return true;
    if (/^РАЗРЕШЕНИЕ СПОРОВ ПО ДОГОВОРУ$/.test(p)) return true;
    if (/^ОТВЕТСТВЕННОСТЬ СТОРОН$/.test(p)) return true;
    if (/^ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ$/.test(p)) return true;
    return false;
  };

  const bottomFadeOpacity = 1;

  return (
    <div className="fixed inset-0 bg-bg z-50">
      {/* Header (fixed) */}
      <div
        ref={headerRef}
        className="fixed top-0 left-0 right-0 px-6 text-center border-b border-stroke bg-bg"
      >
        <div className="absolute left-0 right-0 top-0 h-0.5 bg-white/5">
          <div
            className="h-0.5 bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] transition-all duration-150"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>

        <div className="pt-6 pb-4">
          <div className="mb-3 flex items-center justify-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/5 border border-stroke flex items-center justify-center" style={{ boxShadow: '0 10px 30px rgba(59,130,246,0.22)' }}>
              <img src="/logo.png" alt="Japan Express" className="w-6 h-6 object-contain" />
            </div>
            <div className="text-left leading-tight">
              <div className="text-sm font-semibold text-text-primary">Japan Express</div>
              <div className="text-xs text-text-secondary">miniapp</div>
            </div>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">{TERMS_TITLE}</h2>
          <p className="text-sm text-text-secondary mt-2">{TERMS_SUBTITLE}</p>
        </div>

        <div
          className="pointer-events-none absolute left-0 right-0"
          style={{
            bottom: -1,
            height: HEADER_FADE_HEIGHT,
            background:
              'linear-gradient(180deg, rgba(18,18,18,0) 0%, rgba(18,18,18,0.55) 55%, rgba(18,18,18,1) 100%)',
          }}
        />
      </div>

      {/* Scroll area (only this scrolls) */}
      <div
        ref={scrollRef}
        className="absolute left-0 right-0 overflow-y-auto overflow-x-hidden px-4"
        style={{
          top: headerHeight,
          bottom: footerHeight,
          WebkitOverflowScrolling: 'touch',
          paddingTop: 28,
          paddingBottom: FADE_HEIGHT + 40,
        }}
      >
        <div className="max-w-none text-text-primary leading-relaxed space-y-4" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          {termsParagraphs.map((p, idx) => (
            <p
              key={idx}
              className={
                idx === 0
                  ? 'font-semibold text-[12px] text-text-primary'
                  : isSectionTitle(p)
                    ? 'pt-2 text-[12px] font-semibold tracking-wide text-text-primary/95'
                    : 'text-[12px] text-text-primary/88'
              }
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {p}
            </p>
          ))}
        </div>
      </div>

      {/* Fade overlay above footer (fixed) */}
      <div
        className="fixed left-0 right-0 pointer-events-none z-20"
        style={{
          bottom: footerHeight,
          height: FADE_HEIGHT,
          opacity: bottomFadeOpacity,
          background:
            'linear-gradient(180deg, rgba(18,18,18,0) 0%, rgba(18,18,18,0.70) 45%, rgba(18,18,18,0.92) 72%, rgba(18,18,18,1) 100%)',
        }}
      />

      {/* Footer (fixed) */}
      <div
        ref={footerRef}
        className="fixed bottom-0 left-0 right-0 p-6 bg-bg"
      >
        <button
          disabled={!canAccept}
          onClick={handleAccept}
          className={`
            w-full py-4 px-6 rounded-2xl text-lg font-medium
            transition-all duration-300 ease-in-out shadow-lg
            ${buttonBg} ${buttonText} ${buttonHover} ${buttonCursor}
            disabled:opacity-50
          `}
          style={{ opacity: buttonOpacity }}
        >
          Я прочитал и принимаю условия
        </button>
      </div>
    </div>
  );
};

export default AgreementScreen;
